import {useMemo} from "react";
import {InsightPieChart, InsightPieChartProps} from "../InsightPieChart/InsightPieChart";
import {PieChartData} from "../../chart/PieChart/PieChart";
import {InsightEmpty} from "../InsightEmpty/InsightEmpty";
import {ActionIcon, Flex, Stack} from "@mantine/core";
import {IconSearch} from "@tabler/icons-react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {ExceedsNonPrimaryVolumeStatisticsSnapshotEntity} from "@ots-frontend/ots-data";

export type ExceedsNonPrimaryVolumeByInsightProps =
  Pick<InsightPieChartProps, 'tooltipPos' | 'containerProps' | 'loading' | 'id' | 'mih'>
  & {
  data?: ExceedsNonPrimaryVolumeStatisticsSnapshotEntity[]
  display?: "count" | "volume"
  title?: string
  showEmpty?: boolean
  showSearch?: boolean
}

export const ExceedsNonPrimaryVolumeByInsight = (props: ExceedsNonPrimaryVolumeByInsightProps) => {
  const {
    title,
    data,
    display = "count",
    showEmpty = true,
    showSearch = false,
    ...rest
  } = props

  const {t} = useTranslation(["common"])
  const navigate = useNavigate()

  const chartData: PieChartData[] | undefined = useMemo(() => {
    const chartData: PieChartData[] = []

    if (data) {
      const records: Record<string, number> = {}

      for (const snapshot of data) {
        const bucketName = snapshot.objectName ?? t("common.shared.unknown", "Unknown") ?? "Unknown"
        if (!(bucketName in records)) {
          records[bucketName] = 0
        }

        const value = snapshot.entries.reduce((prev, curr) => prev + (display === "count" ? curr.count : curr.volume), 0)
        records[bucketName] = records[bucketName] + value
      }

      Object.keys(records).forEach((k) => {
        chartData.push({
          name: k,
          value: records[k]
        })
      })
    }

    return chartData
  }, [data, display, t])

  if (showEmpty && (!chartData || chartData.length === 0)) {
    return (
      <InsightEmpty {...rest} title={title ?? t<string>("common.insight.exceedsnonprimaryvolumeby", "High Bottoms By")}>
        {showSearch &&
          <Flex justify="flex-end">
            <ActionIcon onClick={() => navigate("/insights/exceeds-non-primary-volume-by")}>
              <IconSearch size="1.125rem"/>
            </ActionIcon>
          </Flex>
        }
      </InsightEmpty>
    )
  }

  return (
    <InsightPieChart
      {...rest}
      title={title ?? t("common.insight.exceedsnonprimaryvolumeby", "High Bottoms By")}
      data={chartData}>
      {(showSearch) &&
        <Stack>
          {showSearch &&
            <Flex justify="flex-end">
              <ActionIcon onClick={() => navigate("/insights/exceeds-non-primary-volume-by")}>
                <IconSearch size="1.125rem"/>
              </ActionIcon>
            </Flex>
          }
        </Stack>
      }
    </InsightPieChart>
  )
}
